import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MeetingState, TMeetingRecognizeMeta } from "./types";
import {
  MeetingsValue,
  TMeetings,
  TSpeakerContent,
} from "../../../services/api/instances/meetingsApi/types";
import {
  getAudioTranscriptionActionReducerBuilder,
  getMeetingMessagesReducerBuilder,
  meetingChatWithResponsesReducerBuilder,
  postAudioFileActionReducerBuilder,
  startMeetingChatReducerBuilder,
} from "./reducers";
import { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { AssistantValue } from "../../../services/api/instances/assistantsApi/types";

const initialState: MeetingState = {
  meetingValue: null,
  recognizeMeta: {},
  messages: [],
  selectedAssistant: null,
  isMessageLoading: false,
};

const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    setMeetingValue: (state, action: PayloadAction<MeetingsValue>) => {
      state.meetingValue = action.payload;
    },
    setSpeaker: (
      state,
      action: PayloadAction<{ id: string; update: Partial<TSpeakerContent> }>
    ) => {
      if (!state.meetingValue?.meetings_object) return;
      const transcription = state.meetingValue?.meetings_object?.transcription;
      if (!transcription) return;

      state.meetingValue.meetings_object.transcription = transcription.map(
        (speaker) =>
          speaker.id === action.payload.id
            ? { ...speaker, ...action.payload.update }
            : speaker
      );
    },
    updateMeetingValueField: {
      reducer(state: MeetingState, action: PayloadAction<Partial<TMeetings>>) {
        if (!state.meetingValue?.meetings_object) return;
        Object.assign(state.meetingValue.meetings_object, action.payload);
      },
      prepare(updates: Partial<TMeetings>) {
        return { payload: updates };
      },
    },
    setInitRecognizeMeta: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.recognizeMeta[id] = {
        recognize_mode: "upload",
        is_file_loading: false,
        is_recognize: false,
      };
    },
    updateRecognizeMeta: {
      reducer(
        state: MeetingState,
        action: PayloadAction<{
          id: string;
          meta: Partial<TMeetingRecognizeMeta>;
        }>
      ) {
        const { id, meta } = action.payload;

        if (!state.recognizeMeta[id]) {
          state.recognizeMeta[id] = {
            recognize_mode: "info",
            is_recognize: false,
            is_file_loading: false,
          };
        }

        Object.assign(state.recognizeMeta[id], meta);
      },
      prepare(id: string, meta: Partial<TMeetingRecognizeMeta>) {
        return { payload: { id, meta } };
      },
    },
    addUserMessage: {
      reducer(
        state: MeetingState,
        action: PayloadAction<ChatCompletionsMessageObject>
      ) {
        state.messages.push(action.payload);
      },
      prepare(content: string) {
        return {
          payload: {
            role: "user",
            content,
          },
        };
      },
    },
    setMeetingSelectedAssistant: (
      state,
      action: PayloadAction<NormalizedStorageObject<AssistantValue> | null>
    ) => {
      state.selectedAssistant = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    postAudioFileActionReducerBuilder(builder);
    getAudioTranscriptionActionReducerBuilder(builder);
    startMeetingChatReducerBuilder(builder);
    getMeetingMessagesReducerBuilder(builder);
    meetingChatWithResponsesReducerBuilder(builder);
  },
});

export const meetingReducers = meetingSlice.reducer;
export const {
  setMeetingValue,
  setSpeaker,
  updateMeetingValueField,
  setInitRecognizeMeta,
  updateRecognizeMeta,
  setMeetingSelectedAssistant,
  clearMessages,
  addUserMessage,
} = meetingSlice.actions;
